import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { worldCupQueryKeys } from '../constants/worldcupQueryKeys';
import {
  GC_TIME_WORLD_CUP_MS,
  STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
} from '../constants/worldcupReactQuery.constants';

export interface LeaderboardApiEntry {
  id: string;
  userId: string;
  name: string;
  totalPoints: number;
  exactHitCount: number;
  correctResultCount: number;
  winnerCorrect: boolean | null;
  topScorerCorrect: boolean | null;
  winnerTeamId?: string | null;
  topScorerPlayerId?: string | null;
  avatarId?: string | null;
  avatar?: string | null;
  isCurrentUser: boolean;
  rank?: number;
  livePredictions?: {
    matchId: string;
    competitionMatchId: string | null;
    homeTeam: string;
    awayTeam: string;
    homeCountryCode: string;
    awayCountryCode: string;
    liveMinute: number | null;
    kickoffTime: string;
    displayOrder: number | null;
    stage: string;
    prediction: {
      home: number;
      away: number;
    };
    outcome: 'hit' | 'result' | 'miss';
    pointsEarned: number;
    isDoublePoints: boolean;
  }[];
}

interface LeaderboardApiPayload {
  success: boolean;
  error?: string;
  data: {
    entries: LeaderboardApiEntry[];
    currentUser: LeaderboardApiEntry | null;
    totalParticipants: number;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

export function worldCupLeaderboardQueryOptions(
  apiClient: ReturnType<typeof useApiClient>,
  competitionId: string,
  limit: number = 50,
) {
  return {
    queryKey: worldCupQueryKeys.leaderboard(competitionId, { limit }),
    queryFn: async () => {
      const response = await apiClient<LeaderboardApiPayload>(
        `/competitions/${competitionId}/leaderboard?limit=${limit}`,
      );
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to fetch leaderboard');
      }
      return payload.data;
    },
    staleTime: STALE_TIME_WORLD_CUP_LEADERBOARD_MS,
    gcTime: GC_TIME_WORLD_CUP_MS,
    refetchOnWindowFocus: false,
  };
}

export function useWorldCupLeaderboardQuery(
  competitionId: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const apiClient = useApiClient();
  const enabled = options?.enabled ?? true;
  const limit = options?.limit ?? 50;

  return useQuery({
    ...worldCupLeaderboardQueryOptions(apiClient, competitionId, limit),
    enabled,
  });
}
